import { formatMinor, type Adjustment, type ChartAccount } from '@hvac/contracts';
import { Alert, Button, Card, CardBody, CardHeader, Input, Skeleton } from '@hvac/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useState } from 'react';
import {
  adjustmentsQuery,
  chartQuery,
  recordAdjustment,
  reverseAdjustment,
} from '../../shared/api/ledger-queries.js';
import { dollarsToMinor } from './books-format.js';

/**
 * The accountant's adjusting entries.
 *
 * Debits and credits typed straight into the ledger, with a memo saying why.
 * The database refuses an entry that does not balance, one dated in a closed
 * period, and one dated before the books start. An entry is never deleted:
 * it is reversed by an equal and opposite entry that points back at it, and
 * both stay on this list and in the Timeline.
 */

interface Row {
  key: number;
  accountId: string;
  debit: string;
  credit: string;
  description: string;
}

const blankRow = (key: number): Row => ({
  key,
  accountId: '',
  debit: '',
  credit: '',
  description: '',
});

const today = () => new Date().toISOString().slice(0, 10);

export function AdjustmentsCard({
  organizationId,
  canEdit,
}: {
  organizationId: string;
  canEdit: boolean;
}) {
  const queryClient = useQueryClient();
  const adjustments = useQuery(adjustmentsQuery(organizationId));
  const chart = useQuery(chartQuery(organizationId));
  const [editing, setEditing] = useState(false);
  const [entryDate, setEntryDate] = useState(today);
  const [memo, setMemo] = useState('');
  const [rows, setRows] = useState<Row[]>([blankRow(1), blankRow(2)]);
  const [nextKey, setNextKey] = useState(3);
  const [error, setError] = useState<string | null>(null);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['ledger', organizationId] });
    void queryClient.invalidateQueries({ queryKey: ['reports', organizationId] });
  };

  const parsed = rows.map((row) => ({
    row,
    debit: dollarsToMinor(row.debit),
    credit: dollarsToMinor(row.credit),
  }));
  const unreadable = parsed.some(
    ({ debit, credit }) =>
      debit === null || credit === null || debit.startsWith('-') || credit.startsWith('-'),
  );
  const filled = parsed.filter(
    ({ debit, credit }) => (debit ?? '0') !== '0' || (credit ?? '0') !== '0',
  );
  const unplaced = filled.some(({ row }) => row.accountId === '');
  const debits = filled.reduce((sum, { debit }) => sum + BigInt(debit ?? '0'), 0n);
  const credits = filled.reduce((sum, { credit }) => sum + BigInt(credit ?? '0'), 0n);
  const difference = debits - credits;

  const post = useMutation({
    mutationFn: () =>
      recordAdjustment(organizationId, {
        entryDate,
        memo: memo.trim(),
        lines: filled.map(({ row, debit, credit }) => ({
          accountId: row.accountId,
          debitMinor: debit ?? '0',
          creditMinor: credit ?? '0',
          description: row.description.trim() === '' ? null : row.description.trim(),
        })),
      }),
    onSuccess: () => {
      setEditing(false);
      setMemo('');
      setRows([blankRow(1), blankRow(2)]);
      setNextKey(3);
      setError(null);
      refresh();
    },
    onError: (caught) =>
      setError(caught instanceof Error ? caught.message : 'The adjustment was not posted.'),
  });

  const update = (key: number, change: Partial<Row>) =>
    setRows((before) => before.map((row) => (row.key === key ? { ...row, ...change } : row)));

  const list = adjustments.data ?? [];
  const standing = list.filter((entry) => entry.reversedByEntryId === null).length;

  return (
    <Card>
      <CardHeader
        title="Adjusting entries"
        description={
          adjustments.data === undefined
            ? 'Reading…'
            : list.length === 0
              ? 'None yet. An adjustment moves money between accounts without a document behind it: accruals, reclassifications, year-end entries from the accountant. Each carries a memo and is undone only by reversal.'
              : `${String(standing)} standing${list.length === standing ? '' : `, ${String(list.length - standing)} reversed`}. Every one is in the Timeline with who posted it and why.`
        }
      />
      <CardBody className="flex flex-col gap-4">
        {error === null ? null : <Alert tone="danger">{error}</Alert>}

        {adjustments.isPending ? (
          <Skeleton className="h-16 w-full" />
        ) : list.length === 0 ? null : (
          <AdjustmentList
            organizationId={organizationId}
            entries={list}
            canEdit={canEdit}
            onChanged={refresh}
            onError={setError}
          />
        )}

        {!canEdit ? null : !editing ? (
          <div>
            <Button variant="secondary" onClick={() => setEditing(true)}>
              Post an adjustment
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-3 rounded-[var(--radius-control)] border border-border p-3">
            <div className="grid gap-3 sm:grid-cols-[10rem_1fr]">
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-content-muted">Dated</span>
                <input
                  type="date"
                  value={entryDate}
                  onChange={(event) => setEntryDate(event.target.value)}
                  className="rounded-[var(--radius-control)] border border-border bg-surface px-2 py-1"
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-content-muted">Why (this is what the Timeline shows)</span>
                <Input
                  value={memo}
                  onChange={(event) => setMemo(event.target.value)}
                  placeholder="Accrue December's rent, invoice arrived in January"
                />
              </label>
            </div>

            <table className="w-full text-sm tabular">
              <thead>
                <tr className="text-left text-xs text-content-muted">
                  <th className="py-1 font-medium">Account</th>
                  <th className="py-1 font-medium">Line note</th>
                  <th className="w-32 py-1 text-right font-medium">Debit</th>
                  <th className="w-32 py-1 text-right font-medium">Credit</th>
                  <th className="w-8" />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.key}>
                    <td className="py-1 pr-2">
                      <AccountSelect
                        accounts={chart.data ?? []}
                        value={row.accountId}
                        onChange={(accountId) => update(row.key, { accountId })}
                      />
                    </td>
                    <td className="py-1 pr-2">
                      <Input
                        aria-label="Line note"
                        value={row.description}
                        onChange={(event) => update(row.key, { description: event.target.value })}
                      />
                    </td>
                    <td className="py-1 pl-2">
                      <Input
                        aria-label="Debit"
                        inputMode="decimal"
                        className="text-right"
                        value={row.debit}
                        hasError={dollarsToMinor(row.debit) === null}
                        onChange={(event) =>
                          update(row.key, { debit: event.target.value, credit: '' })
                        }
                      />
                    </td>
                    <td className="py-1 pl-2">
                      <Input
                        aria-label="Credit"
                        inputMode="decimal"
                        className="text-right"
                        value={row.credit}
                        hasError={dollarsToMinor(row.credit) === null}
                        onChange={(event) =>
                          update(row.key, { credit: event.target.value, debit: '' })
                        }
                      />
                    </td>
                    <td className="py-1 text-right">
                      <button
                        type="button"
                        aria-label="Remove line"
                        className="text-content-muted hover:text-danger"
                        onClick={() =>
                          setRows((before) => before.filter((other) => other.key !== row.key))
                        }
                      >
                        ×
                      </button>
                    </td>
                  </tr>
                ))}
                <tr className="border-t border-border font-medium">
                  <td colSpan={2} className="py-1">
                    <button
                      type="button"
                      className="text-sm text-brand-700 hover:underline"
                      onClick={() => {
                        setRows((before) => [...before, blankRow(nextKey)]);
                        setNextKey(nextKey + 1);
                      }}
                    >
                      Add a line
                    </button>
                  </td>
                  <td className="py-1 text-right">{formatMinor(debits.toString())}</td>
                  <td className="py-1 text-right">{formatMinor(credits.toString())}</td>
                  <td />
                </tr>
              </tbody>
            </table>

            <p
              className={['text-sm', difference === 0n ? 'text-success' : 'text-warning'].join(' ')}
            >
              {filled.length === 0
                ? 'Enter at least two lines.'
                : difference === 0n
                  ? 'Debits equal credits. It balances.'
                  : `Out by ${formatMinor((difference < 0n ? -difference : difference).toString())}. An adjustment is posted only when it balances.`}
            </p>

            <div className="flex gap-2">
              <Button
                onClick={() => post.mutate()}
                isLoading={post.isPending}
                disabled={
                  entryDate === '' ||
                  memo.trim().length < 3 ||
                  unreadable ||
                  unplaced ||
                  filled.length < 2 ||
                  difference !== 0n
                }
              >
                Post adjustment
              </Button>
              <Button variant="ghost" onClick={() => setEditing(false)}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function AdjustmentList({
  organizationId,
  entries,
  canEdit,
  onChanged,
  onError,
}: {
  organizationId: string;
  entries: readonly Adjustment[];
  canEdit: boolean;
  onChanged: () => void;
  onError: (message: string | null) => void;
}) {
  const [reversing, setReversing] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const reverse = useMutation({
    mutationFn: (entryId: string) => reverseAdjustment(organizationId, entryId, reason.trim()),
    onSuccess: () => {
      setReversing(null);
      setReason('');
      onError(null);
      onChanged();
    },
    onError: (caught) =>
      onError(caught instanceof Error ? caught.message : 'The adjustment was not reversed.'),
  });

  return (
    <table className="w-full text-sm tabular">
      <tbody>
        {entries.map((entry) => {
          const reversed = entry.reversedByEntryId !== null;
          return (
            <Fragment key={entry.entryId}>
              <tr className="border-t border-border">
                <td colSpan={3} className="pt-3 pb-1">
                  <span className="font-medium">{entry.entryDate}</span>{' '}
                  <span className={reversed ? 'text-content-muted line-through' : ''}>
                    {entry.memo ?? 'No memo'}
                  </span>
                  <span className="text-xs text-content-muted">
                    {entry.createdByName === null ? '' : ` · ${entry.createdByName}`}
                    {reversed
                      ? ` · reversed ${entry.reversedOn ?? ''}${entry.reversalMemo === null ? '' : `: ${entry.reversalMemo.replace(/^Reversed: /, '')}`}`
                      : ''}
                  </span>
                </td>
                <td className="pt-3 pb-1 text-right">
                  {!canEdit || reversed || reversing === entry.entryId ? null : (
                    <button
                      type="button"
                      className="text-xs text-content-muted hover:text-danger"
                      onClick={() => {
                        setReversing(entry.entryId);
                        setReason('');
                      }}
                    >
                      Reverse
                    </button>
                  )}
                </td>
              </tr>
              {entry.lines.map((line) => (
                <tr
                  key={`${entry.entryId}:${line.accountId}:${line.debitMinor}:${line.creditMinor}`}
                >
                  <td className="py-0.5 pl-4">
                    <span className="text-content-muted">{line.accountCode}</span>{' '}
                    {line.accountName}
                    {line.description === null ? (
                      ''
                    ) : (
                      <span className="text-content-muted"> · {line.description}</span>
                    )}
                  </td>
                  <td className="w-32 py-0.5 text-right">
                    {line.debitMinor === '0' ? '' : formatMinor(line.debitMinor)}
                  </td>
                  <td className="w-32 py-0.5 text-right">
                    {line.creditMinor === '0' ? '' : formatMinor(line.creditMinor)}
                  </td>
                  <td />
                </tr>
              ))}
              {reversing !== entry.entryId ? null : (
                <tr>
                  <td colSpan={4} className="py-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <Input
                        aria-label="Why reverse it"
                        value={reason}
                        onChange={(event) => setReason(event.target.value)}
                        placeholder="Why it is being reversed"
                        className="min-w-64 flex-1"
                      />
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => reverse.mutate(entry.entryId)}
                        isLoading={reverse.isPending}
                        disabled={reason.trim().length < 3}
                      >
                        Reverse this entry
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setReversing(null)}>
                        Keep it
                      </Button>
                    </div>
                  </td>
                </tr>
              )}
            </Fragment>
          );
        })}
      </tbody>
    </table>
  );
}

const TYPE_LABELS: Readonly<Record<string, string>> = {
  asset: 'Assets',
  liability: 'Liabilities',
  equity: 'Equity',
  revenue: 'Income',
  income: 'Income',
  expense: 'Expenses',
  cogs: 'Cost of sales',
};

function AccountSelect({
  accounts,
  value,
  onChange,
}: {
  accounts: readonly ChartAccount[];
  value: string;
  onChange: (accountId: string) => void;
}) {
  const types = [...new Set(accounts.map((account) => account.accountType))];
  return (
    <select
      aria-label="Account"
      className="w-full rounded-[var(--radius-control)] border border-border bg-surface px-2 py-1 text-sm"
      value={value}
      onChange={(event) => onChange(event.target.value)}
    >
      <option value="">Choose an account…</option>
      {types.map((type) => (
        <optgroup key={type} label={TYPE_LABELS[type] ?? type}>
          {accounts
            .filter((account) => account.accountType === type)
            .map((account) => (
              <option key={account.id} value={account.id}>
                {account.code ?? ''} {account.name}
              </option>
            ))}
        </optgroup>
      ))}
    </select>
  );
}
